import type { DashboardFilters, LaunchPickerParams } from "../models/dashboard";

/** The day the old platform stopped; its warehouse holds nothing after it. */
export const STOP_DAY = "2026-10-18";

/** The widest Date Range a custom pair may span (MPN-2313): the widgets query the warehouse live. */
export const MAX_RANGE_DAYS = 365;

export function daysBefore(day: string, days: number): string {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() - days);
    return date.toISOString().slice(0, 10);
}

/** The same day of the month, `months` earlier; a day the earlier month lacks falls back to its last day. */
export function monthsBefore(day: string, months: number): string {
    const date = new Date(`${day}T00:00:00Z`);
    const dayOfMonth = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - months);
    const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(dayOfMonth, last));
    return date.toISOString().slice(0, 10);
}

/** Whole days from one ISO day to another; negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-09-01" as "Sep 1, 2026"; anything that is not a whole ISO day comes back as given. */
export function formatDay(day: string | null | undefined): string {
    if (!day || !ISO_DAY.test(day)) return day ?? "";
    return new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export interface Preset { key: string; label: string; from: string; to: string }

/** The fixed ranges, counted back from the stop day (MPN-2313): nothing rolls, the data does not move. */
export const RANGE_PRESETS: Preset[] = [
    { key: "3m", label: "Last 3 months of data", from: monthsBefore(STOP_DAY, 3), to: STOP_DAY },
    { key: "6m", label: "Last 6 months of data", from: monthsBefore(STOP_DAY, 6), to: STOP_DAY },
    { key: "12m", label: "Last 12 months of data", from: monthsBefore(STOP_DAY, 12), to: STOP_DAY },
];

/**
 * A custom pair kept inside the rules: neither day after the stop day, `from` not after `to`,
 * and at most MAX_RANGE_DAYS apart. `moved` says which day was just typed, so the other one
 * gives way: typing a From two years back pulls To in to a year after it, and so on.
 */
export function clampRange(from: string, to: string, moved: "from" | "to"): { start_date: string; end_date: string } {
    let start = from > STOP_DAY ? STOP_DAY : from;
    let end = to > STOP_DAY ? STOP_DAY : to;
    if (moved === "from") {
        if (end < start) end = start;
        if (daysBetween(start, end) > MAX_RANGE_DAYS) {
            const limit = daysBefore(start, -MAX_RANGE_DAYS);
            end = limit > STOP_DAY ? STOP_DAY : limit;
        }
    } else {
        if (start > end) start = end;
        if (daysBetween(start, end) > MAX_RANGE_DAYS) start = daysBefore(end, MAX_RANGE_DAYS);
    }
    return { start_date: start, end_date: end };
}

/** The page's defaults: the last three months of data, every type, all EMs and OMs. */
export const DEFAULT_DASHBOARD_FILTERS: DashboardFilters = {
    launch_id: "",
    start_date: RANGE_PRESETS[0].from,
    end_date: RANGE_PRESETS[0].to,
    revenue_type: "All Types",
    engagement_manager: "-1",
    ops_manager: "-1",
};

export const PICKER_KEYS: (keyof LaunchPickerParams)[] = ["revenue_type", "engagement_manager", "ops_manager"];

export function pickerParams(filters: DashboardFilters): LaunchPickerParams {
    return {
        revenue_type: filters.revenue_type,
        engagement_manager: filters.engagement_manager,
        ops_manager: filters.ops_manager,
    };
}

/** Whether any picker filter differs from its default, so the picker can say the pool is narrowed. */
export function pickerNarrowed(filters: DashboardFilters): boolean {
    return PICKER_KEYS.some((key) => filters[key] !== DEFAULT_DASHBOARD_FILTERS[key]);
}

/** The URL carries every filter, so the page reads back exactly what was applied. */
export function filtersFromSearch(params: URLSearchParams): DashboardFilters {
    const filters = { ...DEFAULT_DASHBOARD_FILTERS };
    (Object.keys(filters) as (keyof DashboardFilters)[]).forEach((key) => {
        const value = params.get(key);
        if (value !== null) filters[key] = value;
    });
    return filters;
}

export function searchFromFilters(filters: DashboardFilters): URLSearchParams {
    return new URLSearchParams({ ...filters });
}
